import { TextField } from "./TextField.jsx";

/**
 * Turns any phone text into the saved format, "+19055550123", or "" if it has
 * no digits. Keeps digits only, max 10. A country code is removed only when it
 * is clearly there: a "+1" prefix, or 11 digits starting with 1. Any other
 * leading 0 or 1 is kept, so validation can reject it.
 */
export function formatPhone(text) {
  let d = String(text ?? "")
    .replace(/^\s*\+1/, "")
    .replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1") d = d.slice(1);
  d = d.slice(0, 10);
  return d ? `+1${d}` : "";
}

// "+19055550123" → "9055550123", the part shown after the fixed "+1".
function phoneLocalPart(phone) {
  return String(phone ?? "").replace(/^\+1\s*/, "");
}

/**
 * A phone number input: shows a fixed "+1", accepts digits only, max 10.
 * A number starting with 0 or 1 can be typed; `validatePhone` rejects it.
 *
 * Built for this app, not copied from the design-system kit.
 *
 * `value` and `onChange` use the saved format, "+19055550123" ("" when empty).
 * `onChange` receives that string, not an event.
 *
 * The field doesn't validate:
 * check the number yourself and pass the message in `error`.
 */
export function PhoneInputField({
  value,
  onChange,
  label = "Phone number",
  placeholder = "9055550123",
  required = false,
  ...rest
}) {
  return (
    <TextField
      type="tel"
      autoComplete="tel-national"
      label={label}
      placeholder={placeholder}
      required={required}
      startIcon={<span className="text-base text-ink-2">+1</span>}
      value={phoneLocalPart(value)}
      onChange={(e) => {
        const current = phoneLocalPart(value);
        const typed = e.target.value.replace(/\D/g, "");
        // Full already: ignore an extra digit instead of shifting the number.
        const full = current.length === 10;
        if (full && typed.length > 10 && typed.startsWith(current)) return;
        onChange?.(formatPhone(e.target.value));
      }}
      {...rest}
    />
  );
}
